import { useState } from 'react';
import { useCartStore } from '@/stores/cartStore';
import { useAddToCartModal } from '@/contexts/AddToCartModalContext';
import { useWishlist } from '@/hooks/useWishlist';
import type { ProductCard } from "@/types/shop";
import type { ProductWithVariants } from "@/types/cart";
import { formatPrice } from "@/utils/shopFormatters";
import LocationIcon from "./icons/LocationIcon";
import HeartOutlineIcon from "./icons/HeartOutlineIcon";
import HeartFilledIcon from "./icons/HeartFilledIcon";
import ShieldCheckIcon from "./icons/ShieldCheckIcon";
import ShoppingCartPlus from "./icons/ShoppingCartPlus";
import { truncateName } from "@/utils/truncateName";
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { ModalTriggerSource } from '@/types/cart';
import GlassImage from '../shared/GlassImage';
import { trackEvent } from '@/utils/analytics';
import { userUtils } from '@/utils/auth';

interface ShopProductCardProps {
  product: ProductCard;
  onClick?: () => void;
}

export function ShopProductCard({ product, onClick }: ShopProductCardProps) {
  const { addToCart, openCart } = useCartStore();
  const { openModal } = useAddToCartModal();
  const { isInWishlist, toggleWishlist, isAddingToWishlist, isRemovingFromWishlist } = useWishlist();
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const isProductInWishlist = isInWishlist(product.id);
  const isWishlistLoading = isAddingToWishlist || isRemovingFromWishlist;

  
  

  const handleClick = () => {
    onClick?.();
  };

  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleWishlist(product.id);
    // Track Meta Pixel event only if fbq is available (production environment)
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'AddToWishlist', {
        content_ids: [product.id],
        contents: [
          {
            id: product.id,
            quantity: 1,
            item_price: product.price,
          },
        ],
        content_name: product.title,
        content_type: 'product',
        value: product.price,
        currency: 'NGN',
      });
    }
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    // Stop propagation to prevent card click
    e.stopPropagation();

    const user = userUtils.getUser();

    // If product has variants, open modal instead
    if (product.hasVariants || product.variants?.length > 0) {
      // Convert ProductCard to ProductWithVariants format
      const productWithVariants: ProductWithVariants = {
        _id: product.id,
        id: product.id,
        name: product.title,
        description: product.description || '',
        price: product.price,
        discount: {
          isActive: product.discountPercentage ? product.discountPercentage > 0 : false,
          type: 'percentage',
          value: product.discountPercentage || 0,
          startDate: '',
          endDate: '',
          couponName: '',
          discountedPrice: product.price,
        },
        originalPrice: product.originalPrice,
        discountPercentage: product.discountPercentage || 0,
        category: product.category || '',
        subCategory: product.subCategory || '',
        brand: product.brand || '',
        images: product.image ? [{
          _id: 'img1',
          url: product.image,
          data: product.image,
          contentType: 'image/jpeg',
          resizedImages: [],
        }] : [],
        variants: product.variants || [],
        stock: product.stock || 0,
        lowStockAlert: 5,
        sku: product.sku || '',
        upc: product.upc || '',
        condition: product.condition || 'new',
        weight: product.weight || 1.0,
        minimumPurchaseQuantity: 1,
        seller: {
          _id: product.sellerId,
          personalInfo: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
          },
          storeInfo: {
            storeName: product.sellerName || 'Seller',
            aboutStore: '',
            storeAvatar: {
              url: '',
              public_id: '',
            },
            storeBanner: {
              url: '',
              public_id: '',
            },
            followerCount: 0,
          },
          shippingInfo: {
            warehouseDetails: {
              address: {
                country: '',
                state: '',
                city: '',
                addressLine1: '',
                addressLine2: '',
                zipCode: '',
              },
            },
            offersStorePickup: false,
          },
          verification: {
            isVerified: false,
            accountType: 'individual',
          },
          stats: {
            storeVisits: 0,
            totalProducts: 0,
            totalCustomers: 0,
          },
        },
        shippingInfo: {
          weight: {
            value: product.weight || 1.0,
            unit: 'kg',
          },
          dimensions: {
            length: 0,
            width: 0,
            height: 0,
          },
        },
        returnPolicy: {
          allowed: true,
          periodInDays: 30,
          conditions: [],
        },
        pricing: {
          taxable: true,
          taxRate: 0,
          originalPrice: product.originalPrice,
          discountInfo: {
            isActive: product.discountPercentage ? product.discountPercentage > 0 : false,
            type: 'percentage',
            value: product.discountPercentage || 0,
            startDate: '',
            endDate: '',
            couponName: '',
            discountedPrice: product.price,
          },
        },
        visibility: 'visible',
        status: 'active',
      };

      openModal(productWithVariants, ModalTriggerSource.LISTING);
      return;
    }

    // For products without variants, add directly to cart
    setIsAddingToCart(true);

    try {
      // Simulate API call or any async operation if needed
      await new Promise(resolve => setTimeout(resolve, 300));

      addToCart({
        productId: product.id,
        productName: product.title,
        productImage: product.image,
        price: product.price,
        originalPrice: product.originalPrice,
        discountPercentage: product.discountPercentage || 0,
        quantity: 1,
        sellerId: product.sellerId,
        sellerName: product.sellerName || 'Seller',
        weight: product.weight || 1.0,
      });

      if (user) {
        trackEvent('add_to_cart', {
          userId: user?._id,
          email: user?.email,
          name: user?.firstName + ' ' + user?.lastName,
          username: user?.username,
          country: user?.country,
          isActive: user?.isActive,
          isEmailVerified: user?.emailVerified,
          isPhoneVerified: user?.phoneVerified,
          isDisabled: user?.isDisabled,
          isNewBuyer: user?.isNewBuyer,
          isIdentityVerified: user?.isIdentityVerified,
          productId: product.id,
          productName: product.title,
          productImage: product.image,
          price: product.price,
          originalPrice: product.originalPrice,
          discountPercentage: product.discountPercentage || 0,
          quantity: 1,
          sellerId: product.sellerId,
          sellerName: product.sellerName || 'Seller',
          weight: product.weight || 1.0,
        });
      }

      // Open cart drawer to show the added item
      openCart();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast.error('Failed to add item to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <div
      className="w-full sm:flex-shrink-0 sm:w-[191px] cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative w-full h-[198px] rounded-[5px] overflow-hidden mb-1">
        <GlassImage
          src={product?.image}
          alt={product?.title || "Product image"}
          containerClassName="w-full h-full"
          imageClassName="w-full h-full object-cover"
          placeholderColor="rgba(220, 220, 220, 0.5)"
          tint="light"
          blurIntensity={12}
          showActivityIndicator={true}
          indicatorColor="#16783A"
          loading="lazy"
          decoding="async"
          width={191}
          height={198}
        />
        {product.discountPercentage && (
          <div className="absolute top-2 left-2 bg-[#FFFFFFB3] text-[#F12626] text-[10px] font-lexend font-normal tracking-[-0.10px] capitalize px-2 py-1 rounded-full z-10">
            {product.discountPercentage}% off
          </div>
        )}
        <button
          className="absolute top-2 right-2 flex items-center justify-center rounded-full bg-[#00000033] p-2 hover:bg-[#00000055] transition-colors z-10 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleWishlistToggle}
          disabled={isWishlistLoading}
          aria-label={isProductInWishlist ? "Remove from favorites" : "Add to favorites"}
        >
          {isWishlistLoading ? (
            <Loader2 className="h-[15px] w-[15px] animate-spin text-white" />
          ) : isProductInWishlist ? (
            <HeartFilledIcon width={15} height={15} color="#FF0000" />
          ) : (
            <HeartOutlineIcon width={15} height={15} color="#ffffff" />
          )}
        </button>
        {product.hasProtection && (
          <div className="absolute bottom-2 left-2 bg-primary-green rounded-[5px] px-2 py-1.5 flex items-center gap-2 z-10">
            <ShieldCheckIcon width={12} height={13} color="#ffffff" />
            <span className="text-white text-[10px] font-lexend font-normal tracking-[-0.10px] capitalize">
              auqli buyer protection
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1">
            <LocationIcon width={11} height={13} color="#000000" />
            <span className="text-[0.75rem] font-lexend font-normal tracking-[-0.22px] capitalize text-black">
              {truncateName(product?.location, 12)}
            </span>
          </div>

          <div>
            <button
              onClick={handleAddToCart}
              disabled={isAddingToCart}
              className="font-lexend font-medium bg-[#16783A] hover:bg-black disabled:bg-gray-400 disabled:cursor-not-allowed rounded-full px-4 py-1.5 cursor-pointer transition-colors flex items-center justify-center min-w-[40px]"
              title={product.hasVariants ? "Select options" : "Add to cart"}
            >
              {isAddingToCart ? (
                <Loader2 className="h-3 w-3 animate-spin text-white" />
              ) : (
                <ShoppingCartPlus width={12} height={12} />
              )}
            </button>
          </div>
        </div>

        <h3 className="font-lexend text-[0.875rem] font-normal leading-[1.4] tracking-[-0.14px] capitalize text-[#666666] line-clamp-2">
          {product?.title}
        </h3>
        <div className="flex items-center gap-1">
          <span className="font-lexend text-[1.25rem] font-semibold leading-[0.83] tracking-[-0.22px] capitalize text-[#0f0f0f]">
            {formatPrice(product?.price)}
          </span>
          {product.originalPrice > product.price && (
            <span className="font-lexend text-[12px] font-light tracking-[-0.22px] capitalize text-[#8c8c8c] line-through">
              {formatPrice(product?.originalPrice)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}